// URL classifier used in Getting Started → Automatic onboarding to fast-fail
// URL classes that deterministically produce empty Firecrawl scrapes or no
// business-relevant content. The catalog stays narrow on purpose — adding a
// category means making a deliberate product call about which hosts deserve
// the friction of a blocking notice.

export type KnownBadUrlCategory =
  | 'discord_invite'
  | 'discord_oauth'
  | 'telegram'
  | 'email_address'
  | 'social_platform'
  | 'unsupported_site';

export type KnownBadUrlMatch = {
  category: KnownBadUrlCategory;
  urlHost: string;
};

// Apex + `www` / `m` only. Finer subdomain matching belongs to a category-
// specific handler if it becomes necessary (cf. `discord.com/invite/*` in
// `discord_invite`).
const SOCIAL_PLATFORM_HOSTS = new Set([
  'discord.com',
  'www.discord.com',
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'twitter.com',
  'www.twitter.com',
  'x.com',
  'www.x.com',
  'instagram.com',
  'www.instagram.com',
  'facebook.com',
  'www.facebook.com',
  'm.facebook.com',
  'tiktok.com',
  'www.tiktok.com',
  'github.com',
  'www.github.com',
  'reddit.com',
  'www.reddit.com',
  'linkedin.com',
  'www.linkedin.com',
  // Other social platforms whose root scrape yields generic marketing copy,
  // not a customer business.
  'pinterest.com',
  'www.pinterest.com',
  'snapchat.com',
  'www.snapchat.com',
  'vk.com',
  'www.vk.com',
  'weibo.com',
  'www.weibo.com',
]);

// Hosts that never make sense as a business website to source a KB from,
// regardless of path. Match is host-only (apex + `www`) — narrow on purpose.
// Adding entries means committing to a blocking notice in onboarding, so each
// host should fall cleanly into one of:
//   - search engines (results pages, search-engine docs etc. are not a
//     business),
//   - chat-AI products / our competitors / our parent provider (a user
//     onboarding chatgpt.com or claude.ai is testing the platform, not
//     deploying for real),
//   - encyclopedias / generic info sites,
//   - generic placeholders (`example.com` / RFC 2606),
//   - top-level marketplaces (root only — small sellers on
//     `amazon.com/sellerpage` still go through `social_platform`'s
//     root-only rule).
const UNSUPPORTED_SITE_HOSTS = new Set([
  // Search engines (any path — search-results, settings, docs all yield
  // generic engine copy, never a business). Includes the major non-Western
  // engines / portals (baidu, yandex, naver, and the Chinese portals
  // 163/sina/sohu/qq) where users have pasted their browser-bar URL.
  'google.com',
  'www.google.com',
  'google.co.uk',
  'www.google.co.uk',
  'google.de',
  'www.google.de',
  'google.com.hk',
  'www.google.com.hk',
  'bing.com',
  'www.bing.com',
  'duckduckgo.com',
  'www.duckduckgo.com',
  'yahoo.com',
  'www.yahoo.com',
  'search.yahoo.com',
  'ecosia.org',
  'www.ecosia.org',
  'baidu.com',
  'www.baidu.com',
  'yandex.com',
  'yandex.ru',
  'naver.com',
  'www.naver.com',
  '163.com',
  'www.163.com',
  'sina.com.cn',
  'www.sina.com.cn',
  'sohu.com',
  'www.sohu.com',
  'qq.com',
  'www.qq.com',
  // AI chat products (competitors / our own provider). Onboarding from
  // these is invariably either a joke or someone confusing us with a
  // general-purpose chatbot.
  'chatgpt.com',
  'www.chatgpt.com',
  'openai.com',
  'www.openai.com',
  'claude.ai',
  'www.claude.ai',
  'anthropic.com',
  'www.anthropic.com',
  'gemini.google.com',
  'perplexity.ai',
  'www.perplexity.ai',
  'meta.ai',
  'www.meta.ai',
  'character.ai',
  'www.character.ai',
  'mistral.ai',
  'www.mistral.ai',
  // Encyclopedia / general info — never a business site.
  'wikipedia.org',
  'www.wikipedia.org',
  'en.wikipedia.org',
  'simple.wikipedia.org',
  // RFC 2606 placeholders shown in tutorials and docs.
  'example.com',
  'www.example.com',
  'example.org',
  'www.example.org',
  'example.net',
  'www.example.net',
  // Top-level marketplaces — sellers should onboard their own site, not the
  // marketplace itself. Subdomains like `aws.amazon.com` (AWS docs) are
  // intentionally not blocked.
  'amazon.com',
  'www.amazon.com',
  'ebay.com',
  'www.ebay.com',
  'aliexpress.com',
  'www.aliexpress.com',
  // Messaging-platform homepages and short-link / invite domains. Host-only
  // (any path) because `whatsapp.com/business`, `chat.whatsapp.com/INVITE`,
  // `m.me/SomeOne`, `wa.me/15551234567` are all redirects/landing pages, never
  // a customer business website. PostHog showed users still landing here
  // after the social_platform root-only check (notably `chat.whatsapp.com`
  // invite URLs from May 2026).
  'whatsapp.com',
  'www.whatsapp.com',
  'chat.whatsapp.com',
  'wa.me',
  'www.wa.me',
  'messenger.com',
  'www.messenger.com',
  'm.messenger.com',
  'm.me',
  'www.m.me',
]);

function withScheme(rawUrl: string): string {
  // Match `http` / `https` case-insensitively so `HTTPS://Example.COM` doesn't
  // get a second `https://` prepended.
  return /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
}

export function parseHost(rawUrl: string): string | undefined {
  const trimmed = rawUrl.trim();
  if (!trimmed) return undefined;
  try {
    return new URL(withScheme(trimmed)).hostname.toLowerCase();
  } catch {
    return undefined;
  }
}

function parsePath(rawUrl: string): string | undefined {
  const trimmed = rawUrl.trim();
  if (!trimmed) return undefined;
  try {
    return new URL(withScheme(trimmed)).pathname;
  } catch {
    return undefined;
  }
}

function parseUserInfo(rawUrl: string): string | undefined {
  const trimmed = rawUrl.trim();
  if (!trimmed) return undefined;
  try {
    return new URL(withScheme(trimmed)).username;
  } catch {
    return undefined;
  }
}

export function classifyKnownBadUrl(rawUrl: string): KnownBadUrlMatch | null {
  const host = parseHost(rawUrl);
  if (!host) return null;
  const path = parsePath(rawUrl) ?? '/';

  // `info@chatimize.com` parses as `username=info, hostname=chatimize.com`
  // once the scheme is auto-prepended, so a non-empty `.username` catches
  // both bare emails and `https://`-prefixed user-info URLs.
  if (parseUserInfo(rawUrl)) {
    return { category: 'email_address', urlHost: host };
  }

  if (host === 'discord.gg' || (host === 'discord.com' && path.startsWith('/invite/'))) {
    return { category: 'discord_invite', urlHost: host };
  }
  // Discord OAuth / bot-invite URLs — `discord.com/oauth2/authorize?...` and
  // the API form `discord.com/api/oauth2/...`. Scraping these returns
  // Discord's auth/login page, which then dominates the KB (dhasisaiah@gmail
  // pasted such a URL on 2026-05-23, ended up with a Discord-login bot
  // instead of their game companion). Distinct category from `discord_invite`
  // so the notice copy can tell the user it's their bot-invite link, not a
  // server-invite link. Cover Discord's PTB and Canary builds (same flow on a
  // different subdomain), accept `/oauth2` without a trailing slash, and
  // lowercase the path so a stray `/OAuth2/` doesn't bypass the matcher.
  const lowerPath = path.toLowerCase();
  if (
    (host === 'discord.com' ||
      host === 'www.discord.com' ||
      host === 'canary.discord.com' ||
      host === 'ptb.discord.com') &&
    (lowerPath === '/oauth2' ||
      lowerPath.startsWith('/oauth2/') ||
      lowerPath === '/api/oauth2' ||
      lowerPath.startsWith('/api/oauth2/'))
  ) {
    return { category: 'discord_oauth', urlHost: host };
  }
  if (host === 't.me') {
    return { category: 'telegram', urlHost: host };
  }
  // Root only — a small business pasting `facebook.com/MyBusinessPage` as
  // their de-facto website should still get through.
  if (SOCIAL_PLATFORM_HOSTS.has(host) && (path === '' || path === '/')) {
    return { category: 'social_platform', urlHost: host };
  }
  // Any path — search engines, AI chat products, encyclopedias, and
  // placeholders never resolve to a real business regardless of subpath.
  if (UNSUPPORTED_SITE_HOSTS.has(host)) {
    return { category: 'unsupported_site', urlHost: host };
  }
  return null;
}
